import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// ============================================================================
// SOUND WIZARD STUDIO - BUTTON COMPONENT
// ============================================================================
// Enhanced button component with comprehensive variants and accessibility
// Version: 2.1.0 - Fully accessible, performant, and extensible
// ============================================================================

const buttonVariants = cva(
  [
    // Base styles - optimized for performance and accessibility
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap rounded-md text-sm font-medium",
    "transition-all duration-200 ease-out",
    "disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "active:scale-95 transform-gpu",

    // Icon handling - optimized for performance
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    "[&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200",

    // Loading state styles
    "data-[loading=true]:cursor-wait data-[loading=true]:opacity-75",
  ],
  {
    variants: {
      variant: {
        // Standard variants
        default: [
          "bg-primary text-primary-foreground shadow-xs",
          "hover:bg-primary/90 hover:shadow-sm",
          "active:bg-primary/80"
        ],
        destructive: [
          "bg-destructive text-destructive-foreground shadow-xs",
          "hover:bg-destructive/90 hover:shadow-sm",
          "active:bg-destructive/80"
        ],
        outline: [
          "border border-input bg-background shadow-xs",
          "hover:bg-accent hover:text-accent-foreground hover:shadow-sm",
          "active:bg-accent/80"
        ],
        secondary: [
          "bg-secondary text-secondary-foreground shadow-xs",
          "hover:bg-secondary/80 hover:shadow-sm",
          "active:bg-secondary/70"
        ],
        ghost: [
          "hover:bg-accent hover:text-accent-foreground",
          "active:bg-accent/80"
        ],
        link: [
          "text-primary underline-offset-4",
          "hover:underline hover:text-primary/80",
          "active:text-primary/60"
        ],

        // Cosmic variants - enhanced with better performance
        cosmic: [
          "bg-gradient-to-r from-cyan-500 to-purple-600 text-white",
          "shadow-lg shadow-cyan-500/30",
          "hover:shadow-xl hover:shadow-cyan-500/40",
          "active:shadow-cyan-500/50 active:scale-95",
          "before:absolute before:inset-0 before:rounded-md",
          "before:bg-gradient-to-r before:from-white/20 before:to-transparent",
          "before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-200"
        ],
        mystic: [
          "bg-gradient-to-r from-pink-500 to-red-500 text-white",
          "shadow-lg shadow-pink-500/30",
          "hover:shadow-xl hover:shadow-pink-500/40",
          "active:shadow-pink-500/50 active:scale-95"
        ],
        glass: [
          "bg-white/10 backdrop-blur-sm border border-white/20 text-white",
          "hover:bg-white/20 hover:border-white/30",
          "active:bg-white/30 active:border-white/40"
        ],
        wizard: [
          "bg-gradient-to-r from-purple-600 to-blue-600 text-white",
          "shadow-lg shadow-purple-500/30",
          "hover:shadow-xl hover:shadow-purple-500/40",
          "active:shadow-purple-500/50 active:scale-95"
        ],

        // Success variants
        success: [
          "bg-success text-success-foreground shadow-xs",
          "hover:bg-success/90 hover:shadow-sm",
          "active:bg-success/80"
        ],
        warning: [
          "bg-warning text-warning-foreground shadow-xs",
          "hover:bg-warning/90 hover:shadow-sm",
          "active:bg-warning/80"
        ],
      },
      size: {
        xs: "h-7 px-2 text-xs rounded-sm",
        sm: "h-8 px-3 text-xs rounded-md",
        default: "h-9 px-4 py-2",
        lg: "h-10 px-6 text-base rounded-md",
        xl: "h-12 px-8 text-lg rounded-lg",
        icon: "size-9 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Enhanced Button component with full accessibility and loading states
interface ButtonProps extends
  React.ComponentProps<"button">,
  VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant,
    size,
    loading = false,
    loadingText,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={cn(
          buttonVariants({ variant, size }),
          "relative overflow-hidden",
          className
        )}
        disabled={isDisabled}
        data-loading={loading}
        aria-disabled={isDisabled ? "true" : "false"}
        {...props}
      >
        {/* Loading spinner */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Button content */}
        <div className={cn(
          "flex items-center gap-2 transition-opacity duration-200",
          loading && "opacity-0"
        )}>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {loading ? loadingText || "Loading..." : children}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </div>

        {/* Ripple effect for cosmic variants */}
        {variant?.includes('cosmic') && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants, type ButtonProps }